'use client';
import React from 'react';
import { type FieldValues, type UseFormReturn } from 'react-hook-form';
import Icon from '@/components/ui/AppIcon';
import { SignUpFormData } from './SignUpFlow';

interface StepBankProps {
  form: UseFormReturn<SignUpFormData>;
  formData: Partial<SignUpFormData>;
  onNext: (data: Partial<SignUpFormData>) => void;
  onBack: () => void;
}

const BANKS = [
  'Bank of Saint Lucia',
  'Republic Bank',
  '1st National Bank',
  'RBC Royal Bank',
  'CIBC First Caribbean',
  'Other',
];

export default function StepBank({ form, formData, onNext, onBack }: StepBankProps) {
  const { register, handleSubmit, watch, formState: { errors } } = form;
  const accountValue = watch('accountNumber');
  const selectedBank = watch('bankName');

  const onSubmit = (data: SignUpFormData) => {
    onNext({
      bankName: data.bankName,
      otherBankName: data.otherBankName,
      accountHolderName: data.accountHolderName,
      accountNumber: data.accountNumber,
      confirmAccountNumber: data.confirmAccountNumber,
      bankBranch: data.bankBranch,
      accountType: data.accountType,
    });
  };

  return (
    <div>
      <div className="mb-8">
        <h2 className="font-display font-bold text-2xl text-white mb-2">Bank Account Details</h2>
        <p className="text-[#9CA3AF] text-sm">
          This is where your monthly earnings will be sent. Please ensure all details are accurate.
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
        {/* Bank Name */}
        <div>
          <label className="label">Bank Name</label>
          <select
            className={`input-field ${errors.bankName ? 'input-error' : ''}`}
            defaultValue=""
            {...register('bankName', { required: 'Please select your bank' })}
          >
            <option value="" disabled>Select your bank</option>
            {BANKS.map((bank) => (
              <option key={`bank-${bank}`} value={bank}>{bank}</option>
            ))}
          </select>
          {errors.bankName && <p className="text-[#EF4444] text-xs mt-1.5">{errors.bankName.message}</p>}
        </div>

        {/* Other Bank Name — shown only when "Other" is selected */}
        {selectedBank === 'Other' && (
          <div>
            <label className="label">Bank Name (Other)</label>
            <input
              type="text"
              className={`input-field ${errors.otherBankName ? 'input-error' : ''}`}
              placeholder="Enter the name of your bank"
              {...register('otherBankName', {
                required: selectedBank === 'Other' ? 'Please enter your bank name' : false,
              })}
            />
            {errors.otherBankName && <p className="text-[#EF4444] text-xs mt-1.5">{errors.otherBankName.message}</p>}
          </div>
        )}

        {/* Account Holder Name */}
        <div>
          <label className="label">Account Holder Name</label>
          <p className="text-xs text-[#6B7280] mb-2">Must match the name on your bank account exactly</p>
          <input
            type="text"
            className={`input-field ${errors.accountHolderName ? 'input-error' : ''}`}
            placeholder="As it appears on your bank statement"
            defaultValue={formData.fullName}
            {...register('accountHolderName', { required: 'Account holder name is required' })}
          />
          {errors.accountHolderName && <p className="text-[#EF4444] text-xs mt-1.5">{errors.accountHolderName.message}</p>}
        </div>

        {/* Account Number */}
        <div>
          <label className="label">Account Number</label>
          <input
            type="text"
            className={`input-field ${errors.accountNumber ? 'input-error' : ''}`}
            placeholder="Enter your account number"
            autoComplete="off"
            {...register('accountNumber', {
              required: 'Account number is required',
              minLength: { value: 6, message: 'Enter a valid account number' },
            })}
          />
          {errors.accountNumber && <p className="text-[#EF4444] text-xs mt-1.5">{errors.accountNumber.message}</p>}
        </div>

        {/* Confirm Account Number */}
        <div>
          <label className="label">Confirm Account Number</label>
          <input
            type="text"
            className={`input-field ${errors.confirmAccountNumber ? 'input-error' : ''}`}
            placeholder="Re-enter your account number"
            autoComplete="off"
            {...register('confirmAccountNumber', {
              required: 'Please confirm your account number',
              validate: (val) => val === accountValue || 'Account numbers do not match',
            })}
          />
          {errors.confirmAccountNumber && <p className="text-[#EF4444] text-xs mt-1.5">{errors.confirmAccountNumber.message}</p>}
        </div>

        {/* Account Type */}
        <div>
          <label className="label">Account Type</label>
          <div className="grid grid-cols-2 gap-3 mt-1">
            {(['checking', 'savings'] as const).map((type) => {
              const isSelected = watch('accountType') === type;
              return (
                <label
                  key={type}
                  className={`flex items-center gap-3 p-4 rounded-xl border cursor-pointer transition-colors ${
                    isSelected
                      ? 'border-[#F59E0B] bg-[#F59E0B]/10'
                      : 'border-[#2A2A2A] bg-[#1A1A1A] hover:border-[#3A3A3A]'
                  }`}
                >
                  <input
                    type="radio"
                    value={type}
                    className="sr-only"
                    {...register('accountType', { required: 'Please select an account type' })}
                  />
                  <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                    isSelected ? 'border-[#F59E0B]' : 'border-[#4B5563]'
                  }`}>
                    {isSelected && <div className="w-2 h-2 rounded-full bg-[#F59E0B]" />}
                  </div>
                  <span className={`text-sm font-medium capitalize ${isSelected ? 'text-white' : 'text-[#9CA3AF]'}`}>
                    {type === 'checking' ? 'Checking Account' : 'Savings Account'}
                  </span>
                </label>
              );
            })}
          </div>
          {errors.accountType && <p className="text-[#EF4444] text-xs mt-1.5">{errors.accountType.message}</p>}
        </div>

        {/* Branch */}
        <div>
          <label className="label">Branch</label>
          <input
            type="text"
            className={`input-field ${errors.bankBranch ? 'input-error' : ''}`}
            placeholder="e.g. Castries Main Branch"
            {...register('bankBranch', { required: 'Branch is required' })}
          />
          {errors.bankBranch && <p className="text-[#EF4444] text-xs mt-1.5">{errors.bankBranch.message}</p>}
        </div>

        {/* Security note */}
        <div className="flex items-start gap-3 p-4 bg-[#1A1A1A] border border-[#2A2A2A] rounded-xl">
          <Icon name="LockClosedIcon" size={16} className="text-[#F59E0B] flex-shrink-0 mt-0.5" />
          <p className="text-xs text-[#9CA3AF] leading-relaxed">
            Your banking information is encrypted and stored securely. It is only used for payout disbursements and is never shared with third parties.
          </p>
        </div>

        <div className="flex gap-3 mt-2">
          <button type="button" onClick={onBack} className="btn-secondary flex-1 justify-center py-3.5">
            <Icon name="ArrowLeftIcon" size={18} />
            Back
          </button>
          <button type="submit" className="btn-primary flex-1 justify-center py-3.5">
            Continue
            <Icon name="ArrowRightIcon" size={18} />
          </button>
        </div>
      </form>
    </div>
  );
}